import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { X, Package, Calendar, Clock, AlertTriangle } from 'lucide-react';
import { reduceInventoryEvent } from '../../store/slices/inventorySlice';
import { productosAPI } from '../../services/api';

const ReduceInventoryModal = ({ isOpen, onClose, onSuccess, preselectedItem }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    producto_id: '',
    cantidad: '',
    fecha_evento: new Date().toISOString().split('T')[0],
    hora_evento: new Date().toTimeString().slice(0, 5),
    tipo_rebaja: '',
    observaciones: ''
  });

  // Reset del formulario cuando el modal se abre/cierra
  useEffect(() => {
    if (!isOpen) {
      // Limpiar formulario cuando se cierra el modal
      setFormData({
        producto_id: '',
        cantidad: '',
        fecha_evento: new Date().toISOString().split('T')[0],
        hora_evento: new Date().toTimeString().slice(0, 5),
        tipo_rebaja: '',
        observaciones: ''
      });
      return;
    }

    fetchProducts();
    if (preselectedItem) {
      setFormData(prev => ({
        ...prev,
        producto_id: preselectedItem.producto_id || ''
      }));
    } else {
      // Si no hay item preseleccionado, resetear el formulario
      setFormData({
        producto_id: '',
        cantidad: '',
        fecha_evento: new Date().toISOString().split('T')[0],
        hora_evento: new Date().toTimeString().slice(0, 5),
        tipo_rebaja: '',
        observaciones: ''
      });
    }
  }, [isOpen, preselectedItem]);

  const fetchProducts = async () => {
    try {
      const response = await productosAPI.getAll();
      // Filter out Despacho products - they don't have physical inventory
      const filteredProducts = (response.data || []).filter(p => p.category !== 'Despacho');
      setProducts(filteredProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (tipo_rebaja) => {
    if (!formData.producto_id || !formData.cantidad) {
      alert('Por favor complete todos los campos requeridos');
      return;
    }

    // Validar que la cantidad sea mayor que cero
    const cantidad = parseInt(formData.cantidad);
    if (!cantidad || cantidad <= 0) {
      alert('La cantidad debe ser mayor que cero');
      return;
    }

    setLoading(true);

    try {
      const fechaHora = `${formData.fecha_evento}T${formData.hora_evento}:00`;
      
      const dataToSubmit = {
        producto_id: parseInt(formData.producto_id),
        tipo_evento: 'REBAJAR',
        tipo_rebaja: tipo_rebaja,
        cantidad: cantidad,
        fecha_evento: fechaHora,
        ubicacion: 'Campo',
        observaciones: formData.observaciones || null
      };

      await dispatch(reduceInventoryEvent(dataToSubmit)).unwrap();
      
      // Reset form
      setFormData({
        producto_id: '',
        cantidad: '',
        fecha_evento: new Date().toISOString().split('T')[0],
        hora_evento: new Date().toTimeString().slice(0, 5),
        tipo_rebaja: '',
        observaciones: ''
      });
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error reducing inventory:', error);
      alert(error || 'Error al rebajar inventario');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedProduct = products.find(p => p.id === parseInt(formData.producto_id));
  const currentStock = preselectedItem?.cantidad || 0;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content reduce-inventory-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{t('inventory.reduceInventoryTitle')}</h2>
            {selectedProduct && (
              <p className="modal-subtitle">{selectedProduct.nombre} ({selectedProduct.code})</p>
            )}
          </div>
          <button className="btn-icon" onClick={onClose} type="button">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="modal-body">
          <div className="form-group">
            <label htmlFor="producto_id" className="form-label">
              <Package size={18} />
              {t('inventory.product')}
            </label>
            <select
              id="producto_id"
              name="producto_id"
              value={formData.producto_id}
              onChange={handleChange}
              required
              disabled={!!preselectedItem}
              className="form-input"
            >
              <option value="">{t('inventory.selectProduct')}</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.nombre} ({product.code})
                </option>
              ))}
            </select>
          </div>

          {selectedProduct && (
            <div className="info-box">
              <div className="info-item">
                <span className="label">Stock Actual:</span>
                <span className="value stock-value">{currentStock}</span>
              </div>
            </div>
          )}

          {formData.cantidad && parseInt(formData.cantidad) > 0 && (
            (() => {
              const cantidadRebajar = parseInt(formData.cantidad);
              const stockResultante = currentStock - cantidadRebajar;
              
              if (stockResultante < 0) {
                return (
                  <div className="warning-box warning-box-deficit">
                    <AlertTriangle size={18} />
                    <div>
                      <div className="warning-title">
                        ⚠️ La rebaja dejará el stock en DÉFICIT
                      </div>
                      <div className="warning-details">
                        <span>Stock actual: <strong>{currentStock}</strong></span>
                        <span>Cantidad a rebajar: <strong>-{cantidadRebajar}</strong></span>
                        <span className="stock-negative">Stock resultante: <strong>{stockResultante}</strong></span>
                      </div>
                      <div className="warning-message">
                        El sistema permitirá esta operación, pero el producto quedará marcado como DÉFICIT. 
                        Asegúrese de que esto sea correcto.
                      </div>
                    </div>
                  </div>
                );
              } else if (cantidadRebajar > currentStock) {
                return (
                  <div className="warning-box">
                    <AlertTriangle size={18} />
                    <span>La cantidad a rebajar ({cantidadRebajar}) es mayor que el stock disponible ({currentStock})</span>
                  </div>
                );
              }
              return null;
            })()
          )}

          <div className="form-group">
            <label htmlFor="cantidad" className="form-label">
              <Package size={18} />
              {t('inventory.quantity')}
            </label>
            <input
              type="number"
              id="cantidad"
              name="cantidad"
              value={formData.cantidad}
              onChange={handleChange}
              placeholder={t('inventory.enterQuantity')}
              required
              min="1"
              step="1"
              className="form-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fecha_evento" className="form-label">
                <Calendar size={18} />
                {t('inventory.eventDate')}
              </label>
              <input
                type="date"
                id="fecha_evento"
                name="fecha_evento"
                value={formData.fecha_evento}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="hora_evento" className="form-label">
                <Clock size={18} />
                {t('inventory.eventTime')}
              </label>
              <input
                type="time"
                id="hora_evento"
                name="hora_evento"
                value={formData.hora_evento}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="observaciones" className="form-label">
              {t('inventory.notes')}
            </label>
            <textarea
              id="observaciones"
              name="observaciones"
              value={formData.observaciones}
              onChange={handleChange}
              placeholder={t('inventory.notes')}
              rows="3"
              className="form-input"
            />
          </div>
        </form>

        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit('VENTA')}
            className="btn btn-danger"
            disabled={loading || !formData.producto_id || !formData.cantidad}
          >
            {loading ? t('common.loading') : t('inventory.reduceSaleButton')}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit('MERMA')}
            className="btn btn-warning"
            disabled={loading || !formData.producto_id || !formData.cantidad}
          >
            {loading ? t('common.loading') : t('inventory.reduceLossButton')}
          </button>
        </div>

        <style jsx>{`
          /* Modal Backdrop */
          .modal-backdrop {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: var(--spacing-4);
            animation: fadeIn 0.2s ease-out;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          /* Modal Content */
          .modal-content {
            background: white;
            border-radius: var(--radius-lg);
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            max-width: 550px;
            width: 100%;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            animation: slideUp 0.3s ease-out;
          }

          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Modal Header */
          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: var(--spacing-4) var(--spacing-5);
            border-bottom: 1px solid var(--gray-200);
            background: linear-gradient(135deg, #2c5f2d 0%, #1a4d2e 100%);
            color: white;
            border-radius: var(--radius-lg) var(--radius-lg) 0 0;
            flex-shrink: 0;
          }

          .modal-title {
            font-size: var(--font-size-lg);
            font-weight: 700;
            margin: 0;
            color: white;
          }

          .modal-subtitle {
            font-size: var(--font-size-sm);
            margin: var(--spacing-1) 0 0 0;
            color: rgba(255, 255, 255, 0.9);
          }

          .btn-icon {
            background: rgba(255, 255, 255, 0.1);
            border: none;
            color: white;
            width: 36px;
            height: 36px;
            border-radius: var(--radius-md);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }

          .btn-icon:hover {
            background: rgba(255, 255, 255, 0.2);
          }

          /* Modal Body */
          .modal-body {
            padding: var(--spacing-4) var(--spacing-5);
            overflow-y: auto;
            flex: 1;
            min-height: 0;
          }

          /* Info Box */
          .info-box {
            background-color: #FEF3C7;
            border: 1px solid #FDE68A;
            border-radius: var(--radius-md);
            padding: var(--spacing-3);
            margin-bottom: var(--spacing-4);
          }

          .info-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .info-item .label {
            font-weight: 600;
            color: var(--gray-700);
            font-size: var(--font-size-sm);
          }

          .info-item .stock-value {
            font-weight: 700;
            color: #F59E0B;
            font-size: var(--font-size-lg);
          }

          .warning-box {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
            background-color: #FEF2F2;
            border: 1px solid #FCA5A5;
            color: #DC2626;
            padding: var(--spacing-3);
            border-radius: var(--radius-md);
            margin-bottom: var(--spacing-4);
            font-size: var(--font-size-sm);
          }

          /* Form Groups */
          .form-group {
            margin-bottom: var(--spacing-4);
          }

          .form-group:last-child {
            margin-bottom: 0;
          }

          .form-label {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
            font-size: var(--font-size-sm);
            font-weight: 600;
            color: var(--gray-700);
            margin-bottom: var(--spacing-2);
          }

          .form-input {
            width: 100%;
            padding: 10px var(--spacing-3);
            border: 2px solid var(--gray-300);
            border-radius: var(--radius-md);
            font-size: var(--font-size-sm);
            transition: all 0.2s ease;
            font-family: inherit;
          }

          .form-input:focus {
            outline: none;
            border-color: #2c5f2d;
            box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1);
          }

          .form-input::placeholder {
            color: var(--gray-400);
          }

          .form-input:disabled {
            background-color: var(--gray-100);
            cursor: not-allowed;
          }

          textarea.form-input {
            resize: vertical;
            min-height: 80px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--spacing-4);
          }

          /* Modal Footer */
          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-3);
            padding: var(--spacing-4) var(--spacing-5);
            border-top: 1px solid var(--gray-200);
            background-color: var(--gray-50);
            border-radius: 0 0 var(--radius-lg) var(--radius-lg);
            flex-shrink: 0;
          }

          /* Buttons */
          .btn {
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-2);
            padding: 10px var(--spacing-4);
            border-radius: var(--radius-md);
            font-size: var(--font-size-sm);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            font-family: inherit;
          }

          .btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .btn-outline {
            background-color: white;
            color: var(--gray-700);
            border: 2px solid var(--gray-300);
          }

          .btn-outline:hover:not(:disabled) {
            background-color: var(--gray-50);
            border-color: var(--gray-400);
          }

          .btn-danger {
            background-color: #DC2626;
            color: white;
            border: none;
          }

          .btn-danger:hover:not(:disabled) {
            background-color: #B91C1C;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }

          .btn-warning {
            background-color: #F59E0B;
            color: white;
            border: none;
          }

          .btn-warning:hover:not(:disabled) {
            background-color: #D97706;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }

          .warning-box-deficit {
            background-color: #FEE2E2;
            border-color: #FCA5A5;
            flex-direction: column;
            align-items: flex-start;
          }

          .warning-title {
            font-weight: 700;
            color: #991B1B;
            margin-bottom: var(--spacing-2);
            font-size: var(--font-size-base);
          }

          .warning-details {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
            margin-bottom: var(--spacing-2);
            font-size: var(--font-size-sm);
            color: #7F1D1D;
          }

          .warning-message {
            font-size: var(--font-size-sm);
            color: #7F1D1D;
            line-height: 1.5;
          }

          .stock-negative {
            color: #DC2626;
            font-weight: 700;
          }

          /* Responsive */
          @media (max-width: 640px) {
            .modal-content {
              max-width: 100%;
              max-height: 100vh;
              border-radius: 0;
            }

            .modal-header {
              border-radius: 0;
            }

            .modal-footer {
              border-radius: 0;
              flex-direction: column;
            }

            .form-row {
              grid-template-columns: 1fr;
            }

            .btn {
              width: 100%;
              justify-content: center;
            }
          }

          /* Scrollbar Styling */
          .modal-body::-webkit-scrollbar {
            width: 8px;
          }

          .modal-body::-webkit-scrollbar-track {
            background: var(--gray-100);
            border-radius: var(--radius-md);
          }

          .modal-body::-webkit-scrollbar-thumb {
            background: var(--gray-400);
            border-radius: var(--radius-md);
          }

          .modal-body::-webkit-scrollbar-thumb:hover {
            background: var(--gray-500);
          }
        `}</style>
      </div>
    </div>
  );
};

export default ReduceInventoryModal;
